import { useCallback, useEffect, useRef, useState } from "react";

import { UserRole } from "@eisox/api/core-api";
import { Button, CanAccess, Card, IconButton, Page, Plan, Select, Sidebar } from "@eisox/design-system";
import { GatewayIcon, PanelLeftIcon } from "@eisox/icons";
import { useIdle, useMediaQuery } from "@eisox/tools";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";

import { airHandlerUnitApi, boilerRoomApi, floorApi, gatewayApi, meshApi, roomApi, valveApi } from "~/api";
import { AirHandlerUnit } from "~/components/air-handler-unit";
import BoilerRoom from "~/components/boiler-room";
import { ExpandedAirHandlerUnit } from "~/components/expended-air-handler-unit";
import ExpandedBoilerRoom from "~/components/expended-boiler-room";
import ExpandedGateway from "~/components/expended-gateway";
import { Gateway } from "~/components/gateway";
import { HistoryDatesSlider } from "~/components/history-dates-slider";
import * as LiveHistoryToggle from "~/components/live-history-toggle";
import { Mode } from "~/components/live-history-toggle";
import Multisensor from "~/components/multisensor";
import { PlanItemLink } from "~/components/plan-item-link";
import { Tooltip } from "~/components/tooltip";
import Valve from "~/components/valve";
import { ActionMenu, Display, RightColumn, Room, Settings, useFloorHistory } from "~/features/floor";
import { floorPlanQueries } from "~/features/floor/floor-plan.queries";
import { GatewayDrawer } from "~/features/gateway";
import { MultisensorTooltip } from "~/features/multisensor";
import { getValveRoomData } from "~/features/room";
import { ValveTooltip } from "~/features/valve";
import { notifyCompassError } from "~/helpers/compass.helper";
import { buildingPageHead } from "~/helpers/document-title.helper";
import { isSameHistoryDate, planHistorySearchSchema, toPlanHistorySearch } from "~/helpers/history-search.helper";
import { useStateHistoryDates } from "~/hooks/use-state-history-dates";
import { DataType, DisplaySection, useUserPreferenceStore } from "~/integrations/zustand/stores/user-preference.store";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";
import { FloorProvider, useFloorContext } from "~/providers/floor.provider";

export const Route = createFileRoute("/_authenticated/buildings_/$buildingId/floors_/$floorId")({
  validateSearch: planHistorySearchSchema,
  loader: async ({ context: { queryClient }, params: { floorId } }) => {
    await queryClient.ensureQueryData(floorPlanQueries.byId(floorId));
  },
  head: async ({ match: { context }, params: { buildingId, floorId } }) => {
    const floors = await context.queryClient.ensureQueryData(floorApi.queries.building(buildingId));
    return buildingPageHead(
      context.queryClient,
      buildingId,
      floors.find(floor => floor.id === floorId)?.name ?? m.plan(),
    );
  },
  component: RouteComponent,
});

function Floor({ plan }: { plan: File }) {
  const router = useRouter();
  const navigate = Route.useNavigate();

  const isMobile = useMediaQuery("(max-width: 768px)");
  const isIdle = useIdle({ idleTime: 1000 * 60 * 5 }); // 5 minutes
  const wasIdle = useRef<boolean>(false);

  const queryClient = useQueryClient();

  const display = useUserPreferenceStore(s => s.plan.display);
  const data = useUserPreferenceStore(s => s.plan.data);
  const { mode, date: historyDateFromSearch } = Route.useSearch();
  const history = mode === Mode.History;

  const {
    role,
    permissions,
    building,
    floors,
    rooms,
    roomsGroups,
    gateways,
    valves,
    multisensors,
    boilerRooms,
    airHandlerUnits,
    heatingNetworks,
    meshes,
    modules,
    date,
  } = useBuildingContext(Route.id);
  const {
    floor,
    rooms: floorRooms,
    gateways: floorGateways,
    valves: floorValves,
    multisensors: floorMultisensors,
    boilerRooms: floorBoilerRooms,
    airHandlerUnits: floorAirHandlerUnits,
  } = useFloorContext(Route.id);

  const [now] = useState(() => new Date());

  const replaceHistoryDate = useCallback(
    (date: string) =>
      void navigate({ search: toPlanHistorySearch(Mode.History, date), replace: true, resetScroll: false }),
    [navigate],
  );
  const {
    days: historyDays,
    dates: historyDates,
    isLoading: isHistoryDatesLoading,
  } = useStateHistoryDates({
    enabled: history,
    date: historyDateFromSearch,
    now,
    queryOptions: (startDate, endDate) => floorApi.queries.stateDates(floor.id, startDate, endDate),
    onDateChange: replaceHistoryDate,
  });
  const dateHistory = history ? historyDateFromSearch : undefined;

  const handleDateCommit = useCallback(
    (nextDate: string) => {
      void navigate({
        search: prev => {
          if (isSameHistoryDate(prev.date, nextDate) && prev.mode === Mode.History) {
            return prev;
          }
          return toPlanHistorySearch(Mode.History, nextDate);
        },
        resetScroll: false,
      });
    },
    [navigate],
  );

  const handleModeChange = useCallback(
    (nextMode: Mode) => {
      void navigate({
        search: toPlanHistorySearch(nextMode, nextMode === Mode.History ? historyDateFromSearch : undefined),
        resetScroll: false,
      });
    },
    [historyDateFromSearch, navigate],
  );

  const historyDate = dateHistory ? new Date(dateHistory) : now;

  const {
    valvesHistory,
    gatewaysHistory,
    roomsHistory,
    multisensorsHistory,
    boilerroomsHistory,
    airHandlerUnitsHistory,
  } = useFloorHistory({
    floorId: floor.id,
    building,
    date: dateHistory,
    role,
    mode: history ? Mode.History : Mode.Live,
    valves: floorValves,
    gateways: floorGateways,
    rooms: floorRooms,
    multisensors: floorMultisensors,
    boilerrooms: floorBoilerRooms,
    airHandlerUnits: floorAirHandlerUnits,
  });

  const buildingId = building.id;

  //* Prevent display valves for the visitor
  const valvesDisplayed = display.includes(DisplaySection.VALVES)
    ? valvesHistory.filter(v => v.floor.x && v.floor.y)
    : [];
  const multisensorsDisplayed = display.includes(DisplaySection.MULTISENSORS) ? multisensorsHistory : [];
  const gatewaysDisplayed = display.includes(DisplaySection.GATEWAYS) ? gatewaysHistory : [];
  const roomsDisplayed = display.includes(DisplaySection.ROOMS) ? roomsHistory : [];
  const boilerroomsDisplayed =
    display.includes(DisplaySection.BOILER_ROOMS) && building.pages.boilerRooms ? boilerroomsHistory : [];
  const airHandlerUnitsDisplayed =
    display.includes(DisplaySection.AIR_HANDLER_UNITS) && building.pages.airHandlerUnits ? airHandlerUnitsHistory : [];

  const invalidateQueries = async (buildingId: string) => {
    await Promise.all([
      queryClient.invalidateQueries(floorApi.queries.building(buildingId)),
      queryClient.invalidateQueries(gatewayApi.queries.building(buildingId)),
      queryClient.invalidateQueries(roomApi.queries.building(buildingId)),
      queryClient.invalidateQueries(valveApi.queries.building(buildingId)),
      queryClient.invalidateQueries(boilerRoomApi.queries.building(buildingId)),
      queryClient.invalidateQueries(meshApi.queries.building(buildingId)),
      queryClient.invalidateQueries(airHandlerUnitApi.queries.building(buildingId)),
    ]);
    await router.invalidate();
  };

  useEffect(() => {
    if (history) return;
    if (wasIdle.current && !isIdle) void invalidateQueries(buildingId);
    wasIdle.current = isIdle;
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- invalidateQueries is recreated each render; wake-from-idle is the trigger
  }, [isIdle, history, buildingId]);

  const handleFloorChange = (floorId: string) => {
    void navigate({
      to: "/buildings/$buildingId/floors/$floorId",
      params: { buildingId, floorId },
      search: toPlanHistorySearch(mode),
      resetScroll: false,
    });
  };

  const itemsOnPlan =
    !!floorValves.length ||
    !!floorRooms.length ||
    !!floorGateways.length ||
    !!floorBoilerRooms.length ||
    !!floorMultisensors.length ||
    !!floorAirHandlerUnits.length;

  const someValvesHaveMac = floorValves.some(valve => !!valve.mac);

  const hasBoilerRooms = building.pages.boilerRooms && boilerRooms.length > 0;
  const hasAirHandlerUnits = building.pages.airHandlerUnits && airHandlerUnits.length > 0;

  return (
    <Page.Root>
      <Page.Header className="flex-nowrap *:shrink-0">
        {isMobile && (
          <Sidebar.Trigger asChild>
            <IconButton>
              <PanelLeftIcon />
            </IconButton>
          </Sidebar.Trigger>
        )}
        <Select.Root multiple={false} value={floor.id} onValueChange={handleFloorChange}>
          <Select.Trigger className="max-w-62.5 min-w-0 w-full shrink! overflow-hidden border-none">
            <Select.Value className="min-w-0" />
          </Select.Trigger>
          <Select.Content>
            <Select.Viewport>
              {floors.map(floor => (
                <Select.Item key={floor.id} value={floor.id}>
                  {floor.name}
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Root>
        <CanAccess isAuthorized={permissions.floor?.update}>
          <Settings buildingId={buildingId} floor={floor} permissions={permissions} itemsOnFloor={itemsOnPlan} />
        </CanAccess>
        <div className="gap-10 ml-auto flex justify-center">
          <CanAccess isAuthorized={!isMobile && role === UserRole.INSTALLER}>
            <ActionMenu
              building={building}
              currentFloorId={floor.id}
              floors={floors}
              rooms={rooms}
              roomsGroups={roomsGroups}
              valves={valves}
              gateways={gateways}
              meshes={meshes}
              boilerRooms={boilerRooms}
              multisensors={multisensors}
              airHandlerUnits={airHandlerUnits}
            />
          </CanAccess>
          <CanAccess isAuthorized={permissions.gateway?.read && gateways.length > 0}>
            <GatewayDrawer
              gateways={gateways}
              permissions={permissions}
              valves={valves}
              multisensors={multisensors}
              modules={modules}
            >
              {isMobile ? (
                <IconButton>
                  <GatewayIcon />
                </IconButton>
              ) : (
                <Button>
                  {m.gateways_list()} <GatewayIcon style={{ width: 20, height: 20 }} />
                </Button>
              )}
            </GatewayDrawer>
          </CanAccess>
        </div>
      </Page.Header>
      <Page.Content
        className="gap-2.5 md:mt-0 md:mr-0 md:mb-10 md:ml-10 md:grid-cols-[1fr_290px] md:grid-rows-[auto_1fr] md:gap-5 md:[grid-template-areas:'plan_column'_'plan_column'_'plan_liveHistoryToggle'] min-h-0 grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] grid-rows-[minmax(0,1fr)_auto] rounded-l-[10px] rounded-tr-none rounded-br-none bg-transparent [grid-template-areas:'plan_plan'_'liveHistoryToggle_settings']"
        data-testid="Plan"
      >
        <Card className="p-0 min-h-0 flex h-full w-full flex-col items-center overflow-hidden [grid-area:plan]">
          <div className="min-h-0 relative w-full flex-1">
            <Plan.Root>
              <Plan.Wrapper
                plan={plan}
                className="h-full! w-full! rounded-none border-0"
                northAngle={floor.orientation?.northAngle}
                onCompassError={notifyCompassError}
              >
                {/* ROOMS */}
                {roomsDisplayed.map(r => (
                  <Plan.Item asChild key={r.id} position={{ x: r.floor.x, y: r.floor.y }} origin="bottom center">
                    <PlanItemLink
                      to="/buildings/$buildingId/floors/$floorId/rooms/$roomId"
                      params={{ buildingId, floorId: floor.id, roomId: r.id }}
                      aria-label={r.name}
                    >
                      <Room
                        as="span"
                        displayed={data}
                        name={r.name}
                        airQuality={r.airQuality}
                        humidity={r.humidity}
                        light={r.light}
                        temperature={r.temperature}
                        isTemperatureEstimated={r.isTemperatureEstimated}
                        temperatureValveCount={r.temperatureValveCount}
                        error={r.problemStatus}
                        airQualityV2={r.airQualityV2}
                      />
                    </PlanItemLink>
                  </Plan.Item>
                ))}
                {/* GATEWAYS */}
                {gatewaysDisplayed.map(g => {
                  const name = `${g.uid} - ${g.name}`;
                  return (
                    <Tooltip key={g.id} content={data !== DataType.NAME && name}>
                      <Plan.Item
                        asChild
                        position={{ x: g.floor.x, y: g.floor.y }}
                        origin={data === DataType.NAME ? "bottom center" : "center"}
                      >
                        {data === DataType.NAME ? (
                          <ExpandedGateway error={g.problemStatus}>{name}</ExpandedGateway>
                        ) : (
                          <Gateway error={g.problemStatus} />
                        )}
                      </Plan.Item>
                    </Tooltip>
                  );
                })}
                {/* VALVES */}
                {valvesDisplayed.map(valve => {
                  const roomData = getValveRoomData({ ...valve.stateData, ...valve.sensors });
                  return (
                    <ValveTooltip
                      key={valve.id}
                      valve={valve}
                      roomData={roomData}
                      valves={floorValves}
                      building={building}
                      boilerRooms={boilerRooms}
                      heatingNetworks={heatingNetworks}
                      permissions={permissions}
                    >
                      <Plan.Item asChild position={{ x: valve.floor.x, y: valve.floor.y }}>
                        <Valve error={valve.problemStatus}>{data === DataType.NAME ? valve.uid : undefined}</Valve>
                      </Plan.Item>
                    </ValveTooltip>
                  );
                })}
                {/* MULTISENSOR */}
                {multisensorsDisplayed.map(ms => (
                  <MultisensorTooltip key={ms.id} multisensor={ms} permissions={permissions}>
                    <Plan.Item asChild key={ms.id} position={{ x: ms.floor.x, y: ms.floor.y }}>
                      <Multisensor error={ms.problemStatus} />
                    </Plan.Item>
                  </MultisensorTooltip>
                ))}
                {/* BOILER ROOMS */}
                {boilerroomsDisplayed.map(b => {
                  const boilerRoomContent =
                    data === DataType.NAME ? <ExpandedBoilerRoom>{b.name}</ExpandedBoilerRoom> : <BoilerRoom />;

                  return (
                    <Tooltip content={data !== DataType.NAME && b.name} key={b.id}>
                      {isMobile ? (
                        <Plan.Item
                          asChild
                          position={{ x: b.floor.x, y: b.floor.y }}
                          origin={data === DataType.NAME ? "bottom center" : "center"}
                        >
                          <span aria-label={b.name}>{boilerRoomContent}</span>
                        </Plan.Item>
                      ) : (
                        <Plan.Item
                          asChild
                          position={{ x: b.floor.x, y: b.floor.y }}
                          origin={data === DataType.NAME ? "bottom center" : "center"}
                        >
                          <PlanItemLink
                            to="/buildings/$buildingId/boiler-rooms/$boilerRoomId"
                            params={{ buildingId, boilerRoomId: b.id }}
                            aria-label={b.name}
                          >
                            {boilerRoomContent}
                          </PlanItemLink>
                        </Plan.Item>
                      )}
                    </Tooltip>
                  );
                })}
                {/* AIR HANDLER UNITS */}
                {airHandlerUnitsDisplayed.map(a => (
                  <Tooltip content={data !== DataType.NAME && a.name} key={a.id}>
                    <Plan.Item
                      asChild
                      position={{ x: a.floor.x, y: a.floor.y }}
                      origin={data === DataType.NAME ? "bottom center" : "center"}
                    >
                      <PlanItemLink
                        to="/buildings/$buildingId/air-handler-units/$airHandlerUnitId"
                        params={{ buildingId, airHandlerUnitId: a.id }}
                        aria-label={a.name}
                      >
                        {data === DataType.NAME ? (
                          <ExpandedAirHandlerUnit>{a.name}</ExpandedAirHandlerUnit>
                        ) : (
                          <AirHandlerUnit />
                        )}
                      </PlanItemLink>
                    </Plan.Item>
                  </Tooltip>
                ))}
              </Plan.Wrapper>
              <Plan.Controls className={"top-2.5 left-2.5 absolute z-1"} />
            </Plan.Root>
          </div>

          {history && (
            <HistoryDatesSlider
              isLoading={isHistoryDatesLoading}
              days={historyDays}
              dates={historyDates}
              date={historyDate}
              onDateCommit={handleDateCommit}
            />
          )}
        </Card>
        <RightColumn
          className={"md:flex md:[grid-area:column] hidden"}
          hasBoilerRooms={hasBoilerRooms}
          hasValves={floorValves.length > 0}
          hasMultisensors={floorMultisensors.length > 0}
          hasGateways={floorGateways.length > 0}
          hasRooms={floorRooms.length > 0}
          hasAirHandlerUnits={hasAirHandlerUnits}
        />
        <CanAccess isAuthorized={permissions.history?.valve.histoValve?.read}>
          <Tooltip content={m.no_historic()} openingCondition={!someValvesHaveMac}>
            <LiveHistoryToggle.Root
              className="gap-2.5 md:gap-2 md:rounded-[10px_0_0_10px] md:bg-primary-white md:p-5 min-w-0 md:flex-col md:justify-center md:text-base flex w-full items-center [grid-area:liveHistoryToggle]"
              mode={history ? Mode.History : Mode.Live}
              onModeChange={handleModeChange}
            >
              <LiveHistoryToggle.Tabs historyDisabled={!someValvesHaveMac} />
              <Card className="gap-2.5 px-3 py-2.5 md:p-0 md:shadow-none md:h-auto flex h-full w-full items-center justify-center">
                <LiveHistoryToggle.Timestamp
                  className="md:text-base text-sm whitespace-nowrap"
                  date={history ? historyDate : date}
                  format={isMobile ? "LT" : "L - LTS"}
                />
                <LiveHistoryToggle.Reload onReload={() => invalidateQueries(buildingId)} />
              </Card>
            </LiveHistoryToggle.Root>
          </Tooltip>
        </CanAccess>
        <Display
          className="md:hidden min-w-0 [grid-area:settings]"
          hasBoilerRooms={hasBoilerRooms}
          hasValves={floorValves.length > 0}
          hasMultisensors={floorMultisensors.length > 0}
          hasGateways={floorGateways.length > 0}
          hasRooms={floorRooms.length > 0}
          hasAirHandlerUnits={hasAirHandlerUnits}
        />
      </Page.Content>
    </Page.Root>
  );
}

function RouteComponent() {
  const { floorId } = Route.useParams();

  const { data: plan } = useSuspenseQuery({ ...floorPlanQueries.byId(floorId) });

  return (
    <FloorProvider floorId={floorId}>
      <Floor plan={plan} />
    </FloorProvider>
  );
}
